/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 */
import React, {useMemo} from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage} from 'react-intl';

import {
  AspectRatio,
  Box,
  Flex,
  Image,
  Skeleton,
  Text,
  VStack,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import {useBlogsPagesByTags} from '@amblique/king-living/app/contentful/hooks';
import {
  SEARCH_SPOTLIGHT_BLOG_LIMIT,
  getIdleBlogImageUrl,
  mapPageToSpotlightBlog,
} from '@amblique/king-living/app/components/search/search-spotlight-blog-helpers';
import {listSiblingHover} from '@amblique/king-living/app/theme/components/project/search-typeahead-panel';
import {header2Search} from '@amblique/king-living/app/theme/foundations/header-2-search';
import {ensureArray} from '@amblique/king-living/app/utils/utils';

const DrawerArticleCard = ({blog, onPickBlog}) => {
  const name = blog?.title || blog?.pageTitle || '';
  const src = getIdleBlogImageUrl(blog);
  const pick = (e) => {
    e.preventDefault();
    onPickBlog(blog);
  };

  return (
    <Box
      aria-label={name || 'Recommended article'}
      as="button"
      bg="transparent"
      border="none"
      cursor="pointer"
      display="flex"
      flexDir="column"
      gap={header2Search.space.spotlightCaption}
      m={0}
      p={0}
      tabIndex={0}
      textAlign="left"
      type="button"
      w="full"
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onPickBlog(blog);
        }
      }}
      onMouseDown={pick}
    >
      <Box
        aspectRatio="800 / 533"
        flexShrink={0}
        minH={{base: 'unset', lg: '196px'}}
        overflow="hidden"
        w="full"
      >
        {src ? (
          <Image
            alt={name || ''}
            h="full"
            minH="inherit"
            objectFit="cover"
            src={src}
            w="full"
          />
        ) : (
          <Box bg="brandColors.secondaryBG" h="full" minH="inherit" w="full" />
        )}
      </Box>
      <Text
        as="span"
        display="block"
        noOfLines={4}
        textDecoration="none"
        w="full"
        {...header2Search.type.spotlightTitle}
      >
        {name}
      </Text>
    </Box>
  );
};

DrawerArticleCard.propTypes = {
  blog: PropTypes.object,
  onPickBlog: PropTypes.func.isRequired,
};

/**
 * Articles column: Header.searchTyping.articleTags, or keyword blogs when `blogs` is passed
 * (Header 2.0 search drawer). Stacked Spotlight tiles (Figma 21757:172010); drawer body scrolls.
 */
const SearchRecommendedForYouColumn = ({
  searchTyping,
  isHeaderLoading,
  onPickBlog,
  variant,
  blogs,
}) => {
  const isDrawer = variant === 'drawer';
  const hasBlogOverride = blogs !== undefined;
  const articleTagIds = useMemo(() => {
    if (hasBlogOverride) {
      return [];
    }

    return ensureArray(searchTyping?.articleTags)
      .map((t) => String(t).trim())
      .filter(Boolean);
  }, [hasBlogOverride, searchTyping?.articleTags]);

  const {data: pagesCollection, isLoading: isBlogsByTagsLoading} = useBlogsPagesByTags(
    articleTagIds,
    {
      limit: SEARCH_SPOTLIGHT_BLOG_LIMIT,
      skip: 0,
      useRawArticleTagIds: true,
      queryOptions: {
        enabled: articleTagIds.length > 0,
      },
    }
  );

  const recommendedBlogs = useMemo(() => {
    if (hasBlogOverride) {
      return Array.isArray(blogs) ? blogs : [];
    }

    const items = pagesCollection?.items;

    if (!Array.isArray(items) || items.length === 0) {
      return [];
    }

    return items.map(mapPageToSpotlightBlog).filter(Boolean).slice(0, SEARCH_SPOTLIGHT_BLOG_LIMIT);
  }, [blogs, hasBlogOverride, pagesCollection?.items]);

  const isListLoading = !hasBlogOverride && articleTagIds.length > 0 && isBlogsByTagsLoading;
  const columnWidth = isDrawer ?
    {base: '100%', md: '100%', lg: '294px'} :
    {base: '100%', md: '291px'};
  const cardListProps = {
      align: 'stretch',
      flexDir: 'column',
      gap: 6,
      sx: listSiblingHover,
      w: 'full',
    };

  if (isHeaderLoading && !hasBlogOverride) {
    return (
      <Box
        data-testid="search-recommended-loading"
        flexShrink={0}
        maxW={columnWidth}
        minW={0}
        width={columnWidth}
      >
        <Skeleton height="10px" mb={3} width="200px" />
        <VStack align="stretch" spacing={6} width="full">
          {[0, 1].map((i) => (
            <Box key={i}>
              <AspectRatio ratio={16 / 9} width="full">
                <Skeleton height="full" width="full" />
              </AspectRatio>
              <Skeleton height="14px" mt={3} width="92%" />
            </Box>
          ))}
        </VStack>
      </Box>
    );
  }

  if (!hasBlogOverride && !articleTagIds.length) {
    return null;
  }

  const showBody = isListLoading || recommendedBlogs.length > 0;

  if (!showBody) {
    return null;
  }

  return (
    <Box
      data-testid="search-recommended-for-you"
      display="flex"
      flexDir="column"
      flexShrink={0}
      gap={isDrawer ? header2Search.space.headingToMedia : 0}
      maxW={columnWidth}
      minW={0}
      ml={isDrawer ? {lg: 'auto'} : undefined}
      pl={{base: 0, md: isDrawer ? 0 : 2}}
      width={columnWidth}
    >
      <Text
        {...(isDrawer ?
          header2Search.type.section :
          {
            color: 'rgba(56, 56, 56, 0.60)',
            fontFamily: 'body',
            fontSize: '10px',
            fontWeight: 600,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
          })}
        mb={isDrawer ? 0 : 3}
      >
        {isDrawer ? (
          <FormattedMessage
            defaultMessage="Spotlight"
            id="search.idle.spotlight_heading"
          />
        ) : (
          <FormattedMessage
            defaultMessage="Recommended for you"
            id="search.typing.recommended_heading"
          />
        )}
      </Text>
      {isListLoading && recommendedBlogs.length === 0 ? (
        <Flex {...(isDrawer ? {align: 'stretch', flexDir: 'column', gap: header2Search.space.spotlightList, w: 'full'} : cardListProps)}>
          {[0, 1].map((i) => (
            <Box key={i} flexShrink={0} w="full">
              <AspectRatio ratio={800 / 533} width="full">
                <Skeleton height="full" width="full" />
              </AspectRatio>
              <Skeleton height="14px" mt={2} width="88%" />
            </Box>
          ))}
        </Flex>
      ) : null}
      {recommendedBlogs.length > 0 && isDrawer ? (
        <Flex
          align="stretch"
          data-testid="search-drawer-articles"
          flexDir="column"
          gap={header2Search.space.spotlightList}
          sx={header2Search.listHover}
          w="full"
        >
          {recommendedBlogs.map((blog, index) => (
            <DrawerArticleCard
              key={blog?.pageSysId || blog?.pageSlug || blog?.sys?.id || `recommended-${index}`}
              blog={blog}
              onPickBlog={onPickBlog}
            />
          ))}
        </Flex>
      ) : null}
      {recommendedBlogs.length > 0 && !isDrawer ? (
        <Flex {...cardListProps}>
          {recommendedBlogs.map((blog, index) => {
            const name = blog?.title || blog?.pageTitle || '';
            const src = getIdleBlogImageUrl(blog);

            return (
              <Box
                key={blog?.pageSysId || blog?.pageSlug || blog?.sys?.id || `recommended-${index}`}
                aria-label={name || 'Recommended article'}
                as="button"
                bg="transparent"
                border="none"
                cursor="pointer"
                display="flex"
                flexDir="column"
                m={0}
                p={0}
                role="group"
                tabIndex={0}
                textAlign="left"
                type="button"
                w="full"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onPickBlog(blog);
                  }
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  onPickBlog(blog);
                }}
              >
                <Box
                  borderRadius="sm"
                  overflow="hidden"
                  w="full"
                  _groupHover={{
                    '& img': {
                      transform: 'scale(1.04)',
                    },
                  }}
                  sx={{
                    img: {
                      transition: 'transform 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                    },
                    '@media (prefers-reduced-motion: reduce)': {
                      _groupHover: {
                        '& img': {
                          transform: 'none',
                        },
                      },
                      img: {
                        transition: 'none',
                      },
                    },
                  }}
                >
                  <AspectRatio ratio={16 / 9} w="full">
                    {src ? (
                      <Image
                        alt={name || ''}
                        h="full"
                        objectFit="cover"
                        src={src}
                        w="full"
                      />
                    ) : (
                      <Box bg="blackAlpha.50" h="full" w="full" />
                    )}
                  </AspectRatio>
                </Box>
                <Text
                  as="span"
                  color="brandColors.darkCharcoal"
                  display="block"
                  fontSize="16px"
                  fontWeight="500"
                  lineHeight="130%"
                  mt={3}
                  noOfLines={4}
                  textDecoration="none"
                  w="full"
                >
                  {name}
                </Text>
              </Box>
            );
          })}
        </Flex>
      ) : null}
    </Box>
  );
};

SearchRecommendedForYouColumn.propTypes = {
  blogs: PropTypes.array,
  isHeaderLoading: PropTypes.bool.isRequired,
  onPickBlog: PropTypes.func.isRequired,
  searchTyping: PropTypes.shape({
    articleTags: PropTypes.array,
  }),
  variant: PropTypes.oneOf(['drawer']),
};

export default SearchRecommendedForYouColumn;
